import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Calendar, Clock, MapPin } from 'lucide-react';
import { calendario as calendarioMock } from '../data/mock';
import { api } from '../api/client';
import SocialShare from '../components/SocialShare';
import { buildResolveContext, resolveEventInDay, resolveFinaleHeaderForEvent } from '../components/admin/TornaiEditor';
import { useSiteText } from '../hooks/useSiteText';

const TABS = [
  { key: 'all', label: 'Tutte le categorie' },
  { key: 'pulcini-2015', label: 'Pulcini 2015' },
  { key: 'pulcini-2016', label: 'Pulcini 2016' },
  { key: 'primi-calci', label: 'Primi Calci 2017/18' },
];

// Auto-rilevamento categorie dal testo (fallback lato client). Restituisce un array.
function detectCategorie(day) {
  const out = new Set();
  if (day.categoria) String(day.categoria).split(',').forEach((s) => { const v = s.trim(); if (v) out.add(v); });
  if (out.size > 0) return Array.from(out);
  const haystack = `${day.giorno || ''} ${day.data || ''} ${day.etichetta || ''} ${(day.eventi || []).map((e) => e.evento || '').join(' ')}`.toUpperCase();
  if (haystack.includes('PRIMI CALCI') || /\b201[78]\b/.test(haystack)) out.add('primi-calci');
  if (haystack.includes('2015') || haystack.includes('PULCINI 2015')) out.add('pulcini-2015');
  if (haystack.includes('2016') || haystack.includes('PULCINI 2016')) out.add('pulcini-2016');
  return Array.from(out);
}

const CAT_LABEL = {
  'pulcini-2015': 'Pulcini 2015',
  'pulcini-2016': 'Pulcini 2016',
  'primi-calci': 'Primi Calci 2017/18',
};

export default function Calendario() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [calendario, setCalendario] = useState(calendarioMock);
  const [showAllTab, setShowAllTab] = useState(true);
  const initialTab = searchParams.get('cat') || 'all';
  const [tab, setTab] = useState(initialTab);

  useEffect(() => {
    let alive = true;
    api.getCalendarioFull().then((rows) => {
      if (!alive) return;
      if (Array.isArray(rows) && rows.length > 0) {
        setCalendario(rows.map((r) => ({
          giorno: r.giorno || '',
          data: r.data || '',
          eventi: r.eventi || [],
          etichetta: r.etichetta || r.badge || '',
          categoria: r.categoria || '',
        })));
      }
    }).catch(() => { /* fallback su mock */ });
    api.getSettings().then((s) => {
      if (alive && s && s.calendario_show_all_tab !== undefined) setShowAllTab(s.calendario_show_all_tab !== '0');
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const availableTabs = useMemo(() => {
    const present = new Set();
    calendario.forEach((d) => detectCategorie(d).forEach((c) => present.add(c)));
    const result = TABS.filter((t) => t.key === 'all' || present.has(t.key));
    return showAllTab ? result : result.filter((t) => t.key !== 'all');
  }, [calendario, showAllTab]);

  // Se il tab corrente non è disponibile (es. all nascosto), passa al primo disponibile
  useEffect(() => {
    if (availableTabs.length === 0) return;
    if (!availableTabs.some((t) => t.key === tab)) {
      setTab(availableTabs[0].key);
    }
  }, [availableTabs, tab]);

  const filtered = useMemo(() => {
    if (tab === 'all') return calendario;
    return calendario.filter((d) => detectCategorie(d).includes(tab));
  }, [tab, calendario]);

  // Contesto di risoluzione per auto-compilare semifinali/finale
  // (1° Girone A → nome squadra, Win. Gir. X → vincitore semifinale, ecc.)
  const resolveCtx = useMemo(() => buildResolveContext(calendario), [calendario]);

  const handleTab = (key) => {
    setTab(key);
    setSearchParams(key === 'all' ? {} : { cat: key });
  };

  const title = useSiteText('calendario.title', 'Calendario');
  const subtitle = useSiteText('calendario.subtitle', 'Il programma completo del XIV Memorial Eugenio Abbro — 25 / 31 Maggio 2026');

  return (
    <div className="bg-navy min-h-screen">
      <section className="relative py-24">
        <div className="absolute inset-0 bg-gradient-to-b from-navy-dark to-navy"></div>
        <div className="relative max-w-5xl mx-auto px-4 lg:px-8 text-center">
          <Calendar size={48} className="text-pink-brand mx-auto mb-4" />
          <h1 className="font-brand text-5xl md:text-6xl font-bold text-white mb-4">{title}</h1>
          <div className="w-24 h-1 bg-pink-brand mx-auto rounded"></div>
          <p className="text-white/70 mt-6 max-w-2xl mx-auto">
            {subtitle}
          </p>
          <div className="mt-6 flex justify-center">
            <SocialShare title="Calendario XIV Memorial Eugenio Abbro" />
          </div>
        </div>
      </section>

      {availableTabs.length > 1 && (
        <section className="pb-4">
          <div className="max-w-5xl mx-auto px-3 sm:px-4 lg:px-8">
            <div className="flex flex-wrap gap-2 bg-navy-dark p-1.5 rounded-2xl border border-white/10">
              {availableTabs.map((t) => (
                <button
                  key={t.key}
                  onClick={() => handleTab(t.key)}
                  className={`flex-1 min-w-[140px] py-3 px-3 rounded-xl font-bold text-xs sm:text-sm uppercase tracking-wider transition-all ${
                    tab === t.key ? 'bg-pink-brand text-white shadow-lg' : 'text-white/70 hover:text-white'
                  }`}
                  data-testid={`calendario-tab-${t.key}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="pb-20">
        <div className="max-w-5xl mx-auto px-3 sm:px-4 lg:px-8">
          {filtered.length === 0 ? (
            <div className="glass-card p-10 text-center text-white/60">
              Nessuna giornata per la categoria selezionata.
            </div>
          ) : (
            <div className="relative">
              <div className="absolute left-3 sm:left-4 md:left-6 top-0 bottom-0 w-0.5 bg-pink-brand/40"></div>

              <div className="space-y-6 sm:space-y-8">
                {filtered.map((day, i) => {
                  const etichetta = (day.etichetta || day.badge || '').trim();
                  const hasBadge = !!etichetta;
                  const cats = detectCategorie(day);
                  return (
                    <div key={i} className="relative pl-9 sm:pl-12 md:pl-16">
                      <div
                        className={`absolute left-0 sm:left-0 md:left-2 top-3 w-7 sm:w-8 md:w-9 h-7 sm:h-8 md:h-9 rounded-full ${
                          hasBadge ? 'bg-pink-brand animate-pulse-glow' : 'bg-navy-light border-2 border-pink-brand'
                        } flex items-center justify-center`}
                      >
                        <span className="text-white font-bold text-xs">{i + 1}</span>
                      </div>

                      <div className={`glass-card overflow-hidden ${hasBadge ? 'border-pink-brand' : ''}`}>
                        <div
                          className={`px-3 sm:px-5 py-2.5 sm:py-3 flex items-center justify-between gap-2 ${
                            hasBadge ? 'bg-pink-brand' : 'bg-navy-light'
                          }`}
                        >
                          <div className="min-w-0">
                            <div className="text-white text-[10px] sm:text-xs font-semibold uppercase tracking-widest opacity-80 flex items-center gap-2 flex-wrap">
                              <span>{day.giorno}</span>
                              {tab === 'all' && cats.length > 0 && (
                                <span className="bg-pink-brand/30 border border-pink-brand/50 text-white px-2 py-0.5 rounded text-[9px]">
                                  {cats.map((c) => CAT_LABEL[c] || c).join(' · ')}
                                </span>
                              )}
                            </div>
                            <div className="text-white font-bold text-sm sm:text-lg leading-tight truncate">{(day.data || '').replace(day.giorno || '', '').trim()}</div>
                          </div>
                          {hasBadge && (
                            <span className="bg-white text-pink-brand text-[10px] sm:text-xs font-bold px-2 sm:px-3 py-1 sm:py-1.5 rounded-full uppercase flex-shrink-0">
                              {etichetta}
                            </span>
                          )}
                        </div>
                        <div className="p-3 sm:p-5 space-y-2 sm:space-y-3">
                          {(day.eventi || []).map((e, idx) => {
                            const resolved = resolveEventInDay(e.evento, day, resolveCtx);
                            const finaleHeader = resolveFinaleHeaderForEvent(day.giorno, e.evento, day, resolveCtx);
                            return (
                            <div key={idx} className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-4 p-2 sm:p-3 rounded-lg hover:bg-white/5 transition-colors">
                              <div className="self-start flex items-center gap-1.5 bg-navy-dark px-2.5 py-1 sm:px-3 sm:py-1.5 rounded text-[11px] sm:text-xs font-bold text-white flex-shrink-0 border border-white/10">
                                <Clock size={11} /> {e.ora}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-white font-semibold leading-snug text-sm sm:text-base break-words">{resolved}</div>
                                {finaleHeader && (
                                  <div className="text-pink-brand text-xs sm:text-sm font-semibold mt-1 break-words">
                                    {finaleHeader}
                                  </div>
                                )}
                                {e.luogo && (
                                  <div className="flex items-center gap-1 text-white/60 text-xs sm:text-sm mt-0.5 sm:mt-1">
                                    <MapPin size={11} className="flex-shrink-0" />
                                    <span className="break-words">{e.luogo}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
